import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Checkbox } from '../components/ui/Checkbox';
import { DatePickerShowcase } from './DatePickerShowcase';
import { Button, type ButtonType } from '../components/ui/Button';
import { FieldHelp } from '../components/ui/FieldHelp';
import { FormLine, FormRow, FormSection } from '../components/ui/Form';
import { Icon, iconNames } from '../components/ui/Icon';
import { Input } from '../components/ui/Input';
import { InputGroup } from '../components/ui/InputGroup';
import { InputNumber } from '../components/ui/InputNumber';
import { Upload } from '../components/ui/Upload';
import { Password } from '../components/ui/Password';
import { Message, MessageStack, type MessageType } from '../components/ui/Message';
import { DropdownMenu } from '../components/ui/DropdownMenu';
import { ModuleSidebar } from '../components/ui/ModuleSidebar';
import { ProductNavigation } from '../components/ui/ProductNavigation';
import { findProductModule } from '../components/ui/ProductNavigation/navigation-data';
import { Radio } from '../components/ui/Radio';
import { Dialog } from '../components/ui/Dialog';
import { Drawer, type DrawerLayout } from '../components/ui/Drawer';
import { Link as DesignLink, type LinkType } from '../components/ui/Link';
import { Pagination } from '../components/ui/Pagination';
import { Switch } from '../components/ui/Switch';
import { Popconfirm } from '../components/ui/Popconfirm';
import { Popover } from '../components/ui/Popover';
import { Table, TableActions, type TableColumn } from '../components/ui/Table';
import { Tag, type TagCategory, type TagType } from '../components/ui/Tag';
import { Tooltip } from '../components/ui/Tooltip';
import { Tour, type TourStep } from '../components/ui/Tour';
import { Select } from '../components/ui/Select';
import { ColorPicker } from '../components/ui/ColorPicker';
import { Textarea } from '../components/ui/Textarea';
import { Tabs } from '../components/ui/Tabs';
import { ThemeToggle } from '../components/ui/Theme';
import styles from './ComponentsPage.module.css';

const dropdownShowcaseItems = [
  { icon: 'user-6-line', id: 'detail', label: '查看详情' },
  { disabled: true, id: 'export', label: '导出（无权限）' },
  { id: 'delete', label: '删除课程', separatorBefore: true, danger: true },
] as const;

const dropdownMenuItems = [
  { id: 'early-attendance', label: '提前点名' },
  { id: 'add-student', label: '添加学员' },
  { id: 'remove-student', label: '移除学员' },
  { id: 'cancel-course', label: '取消', separatorBefore: true },
  { id: 'delete-course', label: '删除' },
] as const;

const messageTypes: MessageType[] = ['primary', 'success', 'warning', 'danger', 'info'];

const buttonTypes: ButtonType[] = ['default', 'primary', 'success', 'warning', 'danger', 'info'];

interface TablePreviewRow extends Record<string, unknown> {
  id: number;
  name: string;
  status: string;
  type: string;
}

const tablePreviewRows: TablePreviewRow[] = [
  { id: 1, name: '满 100 减 20', status: '进行中', type: '满减券' },
  { id: 2, name: '新人专享券', status: '未开启', type: '折扣券' },
  { id: 3, name: '暑期课时券', status: '已结束', type: '课时券' },
];

const tablePreviewColumns: TableColumn<TablePreviewRow>[] = [
  { help: '券名称在同一活动内不可重复', key: 'name', sortable: true, title: '券名称' },
  { key: 'type', title: '类型', width: 120 },
  { key: 'actions', render: () => <TableActions actions={[{ label: '编辑', onClick: () => undefined }, { label: '停用', onClick: () => undefined }]} />, title: '操作', width: 140 },
  { filterable: true, filterOptions: [{ label: '进行中', value: '进行中' }, { label: '未开启', value: '未开启' }, { label: '已结束', value: '已结束' }], key: 'status', render: (row) => <Tag type={row.status === '进行中' ? 'success' : row.status === '未开启' ? 'warning' : 'info'}>{row.status}</Tag>, title: '状态', width: 160 },
];

const linkTypes: LinkType[] = ['default', 'primary', 'success', 'warning', 'danger', 'info'];

const tagTypes: TagType[] = ['default', 'neutral', 'success', 'warning', 'danger', 'info'];
const tagCategories: TagCategory[] = ['blue', 'violet', 'cyan'];

const courseStates = [
  { label: '不限', value: '' },
  { label: '进行中', value: 'running' },
  { label: '已结束', value: 'finished' },
] as const;

const courseTeachers = [
  { label: '张老师', value: 'zhang' },
  { label: '李老师', value: 'li' },
  { label: '王老师', value: 'wang' },
  { label: '赵老师', value: 'zhao' },
] as const;

const courseTypes = [
  { group: '学科', label: '语文', value: 'chinese' },
  { group: '学科', label: '数学', value: 'math' },
  { group: '素质', label: '美术', value: 'art' },
  { group: '素质', label: '编程', value: 'coding' },
] as const;

/** 引导示例：三个目标故意摆在不同位置，用来看气泡的翻转与遮罩挖洞。 */
const tourSteps: readonly TourStep[] = [
  { align: 'start', content: '先把遮罩挖在这个目标上，气泡默认出现在下方。', target: '[data-tour="tour-demo-a"]', title: '第一步' },
  { align: 'center', content: '同一个组件换一个目标，高亮框与气泡会跟着走。', side: 'top', target: '[data-tour="tour-demo-b"]', title: '第二步' },
  { align: 'start', content: '最后一步的按钮文案可以换成「知道了」这类更贴切的措辞。', side: 'right', target: '[data-tour="tour-demo-c"]', title: '第三步' },
];

export function ComponentsPage() {
  const [pickerColor, setPickerColor] = useState('#EB6F4A');
  const sidebarGroups = findProductModule('education')?.groups ?? [];
  const [view, setView] = useState('list');
  const [cardOption, setCardOption] = useState('first');
  const [standardTab, setStandardTab] = useState('first');
  const [segmentedTab, setSegmentedTab] = useState('fields');
  const [selectedCities, setSelectedCities] = useState({ beijing: false, shanghai: true });
  const [autoAttendance, setAutoAttendance] = useState(true);
  const [studentName, setStudentName] = useState('');
  const [courseHours, setCourseHours] = useState<number | null>(1);
  const [courseState, setCourseState] = useState('');
  const [clearedState, setClearedState] = useState('running');
  const [clearedInput, setClearedInput] = useState('秋季老带新转介绍卡');
  const [textareaValue, setTextareaValue] = useState('');
  const [courseType, setCourseType] = useState('');
  const [teachers, setTeachers] = useState<string[]>(['zhang', 'li']);
  const [collapsedTeachers, setCollapsedTeachers] = useState<string[]>(['zhang', 'li', 'wang', 'zhao']);
  const [messageOpen, setMessageOpen] = useState(false);
  const [tableSort, setTableSort] = useState<{ key: string; order: 'asc' | 'desc' }>({ key: 'name', order: 'asc' });
  const [tableFilter, setTableFilter] = useState<Record<string, string[]>>({ status: [] });
  const [tableSelection, setTableSelection] = useState<(number | string)[]>([]);
  const [tableCurrent, setTableCurrent] = useState<number | string>(1);
  const [tableLoading, setTableLoading] = useState(false);
  const [tablePage, setTablePage] = useState(1);
  const [paginationPreview, setPaginationPreview] = useState({ page: 3, pageSize: 10 });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [drawerLayout, setDrawerLayout] = useState<DrawerLayout | null>(null);
  const [tourActive, setTourActive] = useState(false);
  const tableRows = [...tablePreviewRows]
    .filter((row) => tableFilter.status.length === 0 || tableFilter.status.includes(row.status))
    .sort((a, b) => (tableSort.order === 'asc' ? String(a[tableSort.key]).localeCompare(String(b[tableSort.key])) : String(b[tableSort.key]).localeCompare(String(a[tableSort.key]))));

  return <main className={styles.page}>
    <header className={styles.header}>
      <Link to="/handoff">返回交付说明</Link>
      <ThemeToggle />
    </header>
    <section className={styles.content}>
      <p className={styles.eyebrow}>COMPONENT OVERVIEW</p>
      <h1>组件预览</h1>
      <p className={styles.summary}>这里展示已登记的公共组件。调整共享样式后，请在本页核对状态与尺寸，再检查所有引用页面。</p>
      <div className={styles.grid}>
        <article className={styles.card} id="tabs">
          <div><p className={styles.label}>RECOMMENDED</p><h2>Tabs</h2></div>
          <p className={styles.usage}>基础下划线页签：切换内容面板。</p>
          <Tabs ariaLabel="基础页签预览" idPrefix="tabs-preview" onChange={setStandardTab} tabs={[{ id: 'first', label: '页签一' }, { id: 'second', label: '页签二' }, { id: 'disabled', label: '未开放', disabled: true }]} value={standardTab} />
          <div aria-labelledby={`tabs-preview-${standardTab}-tab`} className={styles.tabExamplePanel} id={`tabs-preview-${standardTab}-panel`} role="tabpanel">{standardTab === 'first' ? '页签一内容示例' : '页签二内容示例'}</div>
          <p className={styles.usage}>分段式页签：32px 高、4px 圆角；品牌色选中底和白字，支持方向键、Home / End。</p>
          <Tabs ariaLabel="分段式页签预览" idPrefix="segmented-preview" onChange={setSegmentedTab} tabs={[{ id: 'design', label: '页面设计' }, { id: 'fields', label: '字段设置' }]} value={segmentedTab} variant="segmented" />
          <div aria-labelledby={`segmented-preview-${segmentedTab}-tab`} className={styles.tabExamplePanel} id={`segmented-preview-${segmentedTab}-panel`} role="tabpanel">{segmentedTab === 'design' ? '页面设计内容示例' : '字段设置内容示例'}</div>
          <code>src/components/ui/Tabs · variant="segmented"</code>
        </article>
        <article className={styles.navigationCard}>
          <div>
            <p className={styles.label}>RECOMMENDED</p>
            <h2>ProductNavigation</h2>
          </div>
          <p className={styles.usage}>项目页面使用的 PC 顶部模块导航。点击模块可检查当前高亮态与键盘焦点。</p>
          <div className={styles.navigationPreview}>
            <ProductNavigation />
          </div>
          <code>src/components/ui/ProductNavigation</code>
        </article>
        <article className={styles.sidebarCard}>
          <div><p className={styles.label}>RECOMMENDED</p><h2>ModuleSidebar</h2></div>
          <p className={styles.usage}>业务模块下的二级侧边栏。核对分组、Hover、当前项、焦点及收起状态。</p>
          <div className={styles.sidebarPreview}>
            <ModuleSidebar activeItem="课表和点名" ariaLabel="教务管理导航预览" groups={sidebarGroups} />
          </div>
          <code>src/components/ui/ModuleSidebar</code>
        </article>
        <article className={styles.card}>
          <div><p className={styles.label}>RECOMMENDED</p><h2>ThemeToggle</h2></div>
          <ThemeToggle />
          <code>src/components/ui/Theme</code>
        </article>
        <article className={styles.card}>
          <div><p className={styles.label}>RECOMMENDED</p><h2>DropdownMenu</h2></div>
          <p className={styles.usage}>紧凑操作菜单。点击按钮检查选项、分割线、Hover 与键盘焦点。</p>
          <div className={styles.dropdownPreview}>
            <DropdownMenu
              ariaLabel="课程操作预览"
              items={dropdownMenuItems}
              trigger={<button className={styles.previewButton} type="button">操作</button>}
              onItemSelect={() => undefined}
            />
          </div>
          <div className={styles.choicePreview}>
            <DropdownMenu ariaLabel="悬停触发预览" items={dropdownShowcaseItems} onItemSelect={() => undefined} placement="bottomLeft" trigger={<Button type="default">悬停展开</Button>} triggerMode="hover" />
            <DropdownMenu ariaLabel="上方展开预览" items={dropdownShowcaseItems} onItemSelect={() => undefined} placement="topLeft" trigger={<Button type="default">上方展开</Button>} />
            <DropdownMenu ariaLabel="无箭头预览" arrow={false} items={dropdownShowcaseItems} onItemSelect={() => undefined} trigger={<Button type="default">无箭头</Button>} />
          </div>
          <code>src/components/ui/DropdownMenu</code>
        </article>
        <article className={styles.card}>
          <div><p className={styles.label}>RECOMMENDED</p><h2>Button</h2></div>
          <p className={styles.usage}>按 Figma `1-Button 按钮` 核对六种类型、Plain、Default / Large / Small 三种尺寸与禁用状态；悬停变亮、按下变深。</p>
          <div className={styles.choicePreview}>
            {buttonTypes.map((type) => <Button key={type} type={type}>按钮</Button>)}
          </div>
          <div className={styles.choicePreview}>
            <Button leadingIcon="user-6-line">创建课程</Button>
            <Button trailingIcon="a-arrow-right">下一步</Button>
            <Button type="default">取消</Button>
            <Button disabled>不可用</Button>
          </div>
          <div className={styles.choicePreview}>
            {buttonTypes.map((type) => <Button key={`plain-${type}`} plain type={type}>按钮</Button>)}
          </div>
          <div className={styles.choicePreview}>
            <Button leadingIcon="user-6-line" size="large">大按钮</Button>
            <Button leadingIcon="user-6-line">默认按钮</Button>
            <Button leadingIcon="user-6-line" size="small">小按钮</Button>
          </div>
          <code>src/components/ui/Button</code>
        </article>
        <article className={styles.card}>
          <div><p className={styles.label}>RECOMMENDED</p><h2>Radio</h2></div>
          <p className={styles.usage}>按 Figma `2-Radio 单选框` 核对 Large 40 / Default 32 / Small 24 三种尺寸、Border 卡片类型，以及未选中、选中、悬停、禁用四种状态。</p>
          <div aria-label="显示方式预览" className={styles.choicePreview} role="radiogroup">
            <Radio checked={view === 'list'} label="列表显示" name="component-preview-view" onCheckedChange={() => setView('list')} value="list" />
            <Radio checked={view === 'grid'} label="宫格显示" name="component-preview-view" onCheckedChange={() => setView('grid')} value="grid" />
            <Radio checked={false} disabled label="不可用" name="component-preview-disabled" onCheckedChange={() => undefined} value="disabled" />
            <Radio checked disabled label="禁用选中" name="component-preview-disabled-checked" onCheckedChange={() => undefined} value="disabled-checked" />
          </div>
          <div className={styles.choicePreview}>
            <Radio checked label="大尺寸" name="component-preview-large" onCheckedChange={() => undefined} size="large" value="large" />
            <Radio checked label="默认尺寸" name="component-preview-default" onCheckedChange={() => undefined} value="default" />
            <Radio checked label="小尺寸" name="component-preview-small" onCheckedChange={() => undefined} size="small" value="small" />
            <Radio border checked label="卡片选中" name="component-preview-card-on" onCheckedChange={() => undefined} value="card-on" />
            <Radio border checked={false} label="卡片未选" name="component-preview-card-off" onCheckedChange={() => undefined} value="card-off" />
          </div>
          <p className={styles.usage}>卡片单选变体：提供选中边框、勾选角标、键盘焦点和禁用状态；内容和尺寸由调用方决定。</p>
          <div aria-label="卡片单选预览" className={styles.choicePreview} role="radiogroup">
            <Radio aria-label="卡片选项一" checked={cardOption === 'first'} className={styles.radioCardPreview} label="选项一" name="component-preview-card-option" onCheckedChange={() => setCardOption('first')} value="first" variant="card" />
            <Radio aria-label="卡片选项二" checked={cardOption === 'second'} className={styles.radioCardPreview} label="选项二" name="component-preview-card-option" onCheckedChange={() => setCardOption('second')} value="second" variant="card" />
            <Radio aria-label="禁用卡片选项" checked={false} className={styles.radioCardPreview} disabled label="不可用" name="component-preview-card-option" onCheckedChange={() => undefined} value="disabled" variant="card" />
          </div>
          <code>src/components/ui/Radio</code>
        </article>
        <article className={styles.card}>
          <div><p className={styles.label}>RECOMMENDED</p><h2>Checkbox</h2></div>
          <p className={styles.usage}>按 Figma `2-Checkbox 多选框` 核对三种尺寸、Border 卡片类型，以及未选中、选中、半选、禁用四种状态。</p>
          <div className={styles.choicePreview}>
            <Checkbox checked={selectedCities.shanghai} label="上海" onCheckedChange={(checked) => setSelectedCities((cities) => ({ ...cities, shanghai: checked }))} value="shanghai" />
            <Checkbox checked={selectedCities.beijing} label="北京" onCheckedChange={(checked) => setSelectedCities((cities) => ({ ...cities, beijing: checked }))} value="beijing" />
            <Checkbox checked={false} indeterminate label="部分选择" onCheckedChange={() => undefined} value="partial" />
            <Checkbox checked={false} disabled label="不可用" onCheckedChange={() => undefined} value="disabled" />
            <Checkbox checked disabled label="禁用选中" onCheckedChange={() => undefined} value="disabled-checked" />
          </div>
          <div className={styles.choicePreview}>
            <Checkbox checked label="大尺寸" onCheckedChange={() => undefined} size="large" />
            <Checkbox checked label="默认尺寸" onCheckedChange={() => undefined} />
            <Checkbox checked label="小尺寸" onCheckedChange={() => undefined} size="small" />
            <Checkbox border checked label="卡片选中" onCheckedChange={() => undefined} />
            <Checkbox border checked={false} label="卡片未选" onCheckedChange={() => undefined} />
          </div>
          <code>src/components/ui/Checkbox</code>
        </article>
        <article className={styles.card}>
          <div><p className={styles.label}>RECOMMENDED</p><h2>Switch</h2></div>
          <p className={styles.usage}>按 Figma `2-Switch 开关` 核对 Large 50×24 / Default 40×20 / Small 30×16 三种尺寸，以及开、关、禁用与 Text 类型。</p>
          <div className={styles.choicePreview}>
            <Switch checked={autoAttendance} label="大尺寸开关" size="large" onCheckedChange={setAutoAttendance} />
            <Switch checked={autoAttendance} label="默认尺寸开关" onCheckedChange={setAutoAttendance} />
            <Switch checked={autoAttendance} label="小尺寸开关" onCheckedChange={setAutoAttendance} size="small" />
          </div>
          <div className={styles.choicePreview}>
            <Switch checked label="默认开启" onCheckedChange={() => undefined} />
            <Switch checked={false} label="默认关闭" onCheckedChange={() => undefined} />
            <Switch checked disabled label="禁用开启" onCheckedChange={() => undefined} />
            <Switch checked={false} disabled label="禁用关闭" onCheckedChange={() => undefined} />
          </div>
          <div className={styles.choicePreview}>
            <Switch checked checkedText="开" label="文字开关" onCheckedChange={() => undefined} type="text" uncheckedText="关" />
            <Switch checked={false} checkedText="开" label="文字开关" onCheckedChange={() => undefined} type="text" uncheckedText="关" />
          </div>
          <code>src/components/ui/Switch</code>
        </article>
        <article className={styles.card}>
          <div><p className={styles.label}>RECOMMENDED</p><h2>Input</h2></div>
          <p className={styles.usage}>按 Figma `2-Input 输入框` 核对 Large 40 / Default 32 / Small 24 三种尺寸，以及默认、悬停、焦点、错误、禁用五种状态与提示文案。</p>
          <div className={styles.choicePreview}>
            <Input ariaLabel="大尺寸输入框预览" onValueChange={() => undefined} placeholder="Large 40" size="large" value="" />
            <Input ariaLabel="学员姓名预览" onValueChange={setStudentName} placeholder="请输入学员姓名" value={studentName} />
            <Input ariaLabel="小尺寸输入框预览" onValueChange={() => undefined} placeholder="Small 24" size="small" value="" />
          </div>
          <div className={styles.choicePreview}>
            <Input ariaLabel="带图标输入框预览" leadingIcon="user-6-line" onValueChange={() => undefined} placeholder="带前缀图标" value="" />
            <Input ariaLabel="禁用输入框预览" disabled onValueChange={() => undefined} value="不可编辑" />
            <Input ariaLabel="错误输入框预览" error onValueChange={() => undefined} placeholder="请输入金额" prompt="请输入正确的金额" value="" />
            <Input ariaLabel="提示输入框预览" onValueChange={() => undefined} placeholder="支持中英文" prompt="支持中英文与数字" value="" />
          </div>
          <div className={styles.choicePreview}>
            <Input ariaLabel="前缀槽预览" onValueChange={() => undefined} prefix={<span>￥</span>} placeholder="请输入金额" value="" />
            <Input ariaLabel="后缀槽预览" onValueChange={() => undefined} placeholder="请输入天数" suffix={<span>天</span>} value="30" />
            <Input allowClear ariaLabel="可清空输入框预览" onValueChange={setClearedInput} value={clearedInput} />
            <Input ariaLabel="回车提交预览" onPressEnter={() => undefined} onValueChange={() => undefined} placeholder="回车触发查询" value="" />
          </div>
          <code>src/components/ui/Input</code>
        </article>
        <article className={styles.card}>
          <div><p className={styles.label}>RECOMMENDED</p><h2>Password</h2></div>
          <p className={styles.usage}>按 Figma `2-Input 输入框 / Password` 核对尾部 view / hide 显隐切换与 Display=on/off 变体；三尺寸与五态同 `Input`，支持 `allowClear`、清空按钮排在显隐切换之前；`lock` 前缀图标 Iconfont 暂缺，需要时经 `leadingIcon` / `prefix` 槽传入。</p>
          <div className={styles.choicePreview}>
            <Password ariaLabel="密码预览" onValueChange={() => undefined} placeholder="请输入密码" value="" />
            <Password ariaLabel="明文密码预览" defaultVisible onValueChange={() => undefined} value="w1-design" />
            <Password allowClear ariaLabel="可清空密码预览" onValueChange={() => undefined} value="Autumn2026" />
          </div>
          <div className={styles.choicePreview}>
            <Password ariaLabel="大尺寸密码预览" onValueChange={() => undefined} placeholder="Large 40" size="large" value="" />
            <Password ariaLabel="小尺寸密码预览" onValueChange={() => undefined} placeholder="Small 24" size="small" value="" />
            <Password ariaLabel="禁用密码预览" disabled onValueChange={() => undefined} value="不可编辑" />
          </div>
          <code>src/components/ui/Password</code>
        </article>
        <article className={styles.card}>
          <div><p className={styles.label}>RECOMMENDED</p><h2>Upload</h2></div>
          <p className={styles.usage}>内置原生文件选择器；业务通过 accept、multiple、maxCount、maxSize 配置格式、数量和大小。按 Figma Upload 展示普通文件列表与图片卡片的完成、预览和删除状态。</p>
          <div className={styles.choicePreview}>
            <Upload accept=".pdf,.doc,.docx" ariaLabel="上传文件预览" description="文件格式与数量由业务配置。" files={[{ id: 'demo-file', name: 'recommendation-card.pdf' }]} onRemove={() => undefined} onSelect={() => undefined} />
            <Upload accept="image/*" ariaLabel="上传图片卡片预览" files={[{ id: 'demo-image', name: '推荐卡封面', url: './brand/xiaogj-nav-logo.png' }]} maxCount={3} multiple onPreview={() => undefined} onRemove={() => undefined} onSelect={() => undefined} variant="picture-card" />
            <Upload accept="image/*" ariaLabel="紧凑上传预览" maxCount={1} onSelect={() => undefined} size="compact" variant="picture-card" />
          </div>
          <code>src/components/ui/Upload</code>
        </article>
        <article className={styles.card}>
          <div><p className={styles.label}>RECOMMENDED</p><h2>Textarea</h2></div>
          <p className={styles.usage}>按 Figma `2-Input 输入框 / Textarea` 核对 280×80 与右下角拖拽手柄、五种状态，以及 12px 右对齐计数；字数上限按 Ant Design `count.max` 口径，超出只标红不截断输入。</p>
          <div className={styles.choicePreview}>
            <Textarea ariaLabel="长文本预览" onValueChange={() => undefined} placeholder="请输入" value="" />
            <Textarea ariaLabel="带计数长文本预览" maxLength={100} onValueChange={setTextareaValue} showCount value={textareaValue} />
            <Textarea ariaLabel="不可拖拽长文本预览" onValueChange={() => undefined} placeholder="固定高度" resizable={false} value="" />
          </div>
          <div className={styles.choicePreview}>
            <Textarea ariaLabel="提示长文本预览" onValueChange={() => undefined} prompt="老师转发活动时展示的备注信息" value="" />
            <Textarea ariaLabel="错误长文本预览" error onValueChange={() => undefined} prompt="最多 500 字" value="" />
            <Textarea ariaLabel="禁用长文本预览" disabled onValueChange={() => undefined} showCount value="不可编辑" />
          </div>
          <code>src/components/ui/Textarea</code>
        </article>
        <article className={styles.card}>
          <div><p className={styles.label}>RECOMMENDED</p><h2>DatePicker</h2></div>
          <DatePickerShowcase />
          <code>src/components/ui/DatePicker</code>
        </article>
        <article className={styles.card}>
          <div><p className={styles.label}>RECOMMENDED</p><h2>InputGroup</h2></div>
          <p className={styles.usage}>用于货币符号、单位、网址前后缀等短附加内容；附加区使用浅灰底，与输入框拼接。长句子使用普通文字穿插独立输入框，不把整句话加框。</p>
          <div className={styles.choicePreview}>
            <InputGroup frontSlot="¥">
              <Input ariaLabel="订单门槛金额预览" onValueChange={() => undefined} placeholder="请输入" value="" />
            </InputGroup>
            <InputGroup rearSlot="元">
              <Input ariaLabel="优惠金额预览" onValueChange={() => undefined} placeholder="请输入" value="" />
            </InputGroup>
            <InputGroup frontSlot="https://" rearSlot=".com">
              <Input ariaLabel="网址预览" onValueChange={() => undefined} placeholder="请输入域名" value="" />
            </InputGroup>
          </div>
          <code>src/components/ui/InputGroup</code>
        </article>
        <article className={styles.card}>
          <div><p className={styles.label}>RECOMMENDED</p><h2>FormRow / FormSection</h2></div>
          <p className={styles.usage}>表单行版式：标签栏 150 右对齐、必填星号、问号说明、提示行，`layout` 分单行与堆叠两种；分节标题可带徽标与分隔线。标签用 `div`，字段的可访问名称由控件自身的 `ariaLabel` 提供。</p>
          <div className={styles.formPreview}>
            <FormRow help="展示在电子推荐卡活动页与微信分享卡片上的活动名称。" helpLabel="活动名称说明" label="活动名称" required>
              <Input ariaLabel="活动名称预览" onValueChange={() => undefined} placeholder="请输入活动名称" value="" />
            </FormRow>
            <FormRow hint="支持中英文与数字" label="活动时间" required>
              <Input ariaLabel="活动开始时间预览" onValueChange={() => undefined} type="date" value="" />
              <span aria-hidden="true">-</span>
              <Input ariaLabel="活动结束时间预览" onValueChange={() => undefined} type="date" value="" />
            </FormRow>
            <FormRow error hint="请输入正确的金额" label="优惠金额" required>
              <InputNumber ariaLabel="优惠金额预览" min={0} onValueChange={() => undefined} value={null} />
            </FormRow>
            <FormRow label="使用条件" layout="stack" required>
              <FormLine>
                <Radio checked label="报名后即可使用" name="preview-use-condition" onCheckedChange={() => undefined} />
              </FormLine>
              <FormLine>
                <Radio checked={false} label="消课后可用" name="preview-use-condition" onCheckedChange={() => undefined} />
                <span className={styles.inlineSentence}>
                  <span>报名并消课</span>
                  <Input ariaLabel="消课数量预览" className={styles.inlineInput} onValueChange={() => undefined} placeholder="请输入" value="" />
                  <span>课时后方可使用</span>
                </span>
              </FormLine>
            </FormRow>
            <FormSection badge={<Tag topic="dark" type="danger">必填</Tag>} help="老学员把电子推荐卡分享给新学员后可获得的奖励。" helpLabel="老学员推荐奖励说明" title="老学员推荐奖励" />
            <FormSection divider title="报名设置" />
          </div>
          <code>src/components/ui/Form</code>
        </article>
        <article className={styles.card}>
          <div><p className={styles.label}>RECOMMENDED</p><h2>FieldHelp</h2></div>
          <p className={styles.usage}>字段说明问号：16px 命中区 + 线条问号 `bangzhuzhongxin`（搭配 14px 文字），悬停显示说明、可键盘聚焦；表头列头与表单标签共用，可访问名由调用方给全。</p>
          <div className={styles.choicePreview}>
            <span>悬停查看说明<FieldHelp content="展示在电子推荐卡活动页与微信分享卡片上的活动名称。" label="活动名称说明" /></span>
            <span>另一个字段<FieldHelp content="开启后，学员全额退费时已使用的优惠券会自动返还。" label="全额退费返券说明" /></span>
          </div>
          <code>src/components/ui/FieldHelp</code>
        </article>
        <article className={styles.card}>
          <div><p className={styles.label}>RECOMMENDED</p><h2>InputNumber</h2></div>
          <p className={styles.usage}>按 Figma `2-Input Number 数字输入框` 核对 Large 180×40 / Default 160×32 / Small 130×24 三种尺寸与左右两种步进布局。</p>
          <div className={styles.choicePreview}>
            <InputNumber ariaLabel="大尺寸数字输入框预览" onValueChange={() => undefined} size="large" value={1} />
            <InputNumber ariaLabel="课时预览" max={10} min={0} onValueChange={setCourseHours} value={courseHours} />
            <InputNumber ariaLabel="小尺寸数字输入框预览" onValueChange={() => undefined} size="small" value={1} />
          </div>
          <div className={styles.choicePreview}>
            <InputNumber ariaLabel="右侧步进预览" onValueChange={() => undefined} placement="right" value={1} />
            <InputNumber ariaLabel="禁用数字输入框预览" disabled onValueChange={() => undefined} value={1} />
            <InputNumber ariaLabel="禁用右侧步进预览" disabled onValueChange={() => undefined} placement="right" value={1} />
          </div>
          <code>src/components/ui/InputNumber</code>
        </article>
        <article className={styles.card}>
          <div><p className={styles.label}>RECOMMENDED</p><h2>Select</h2></div>
          <p className={styles.usage}>按 Figma `2-Select 选择器` 核对 Large 40 / Default 32 / Small 24 三种触发器尺寸、浮层箭头、选项悬停与选中态（单选仅用主题色文字），并按 Ant Design 补齐占位、清空、错误、加载、空数据、分组与多选标签（含 `+N` 折叠）。</p>
          <div className={styles.choicePreview}>
            <Select ariaLabel="课程状态预览" onValueChange={setCourseState} options={courseStates} value={courseState} />
            <Select ariaLabel="大尺寸选择器预览" onValueChange={() => undefined} options={courseStates} size="large" value="running" />
            <Select ariaLabel="小尺寸选择器预览" onValueChange={() => undefined} options={courseStates} size="small" value="" />
            <Select ariaLabel="禁用选择器预览" disabled onValueChange={() => undefined} options={courseStates} value="" />
            <Select
              ariaLabel="授课老师预览"
              dialogContent={({ select }) => <Button onClick={() => select('teacher-zhang')}>张老师</Button>}
              dialogTitle="选择授课老师"
              mode="dialog"
              onValueChange={() => undefined}
              options={[{ label: '张老师', value: 'teacher-zhang' }]}
              value=""
            />
          </div>
          <div className={styles.choicePreview}>
            <Select ariaLabel="占位文案预览" onValueChange={() => undefined} options={[]} placeholder="请选择课程状态" value="" />
            <Select allowClear ariaLabel="可清空选择器预览" onValueChange={setClearedState} options={courseStates} value={clearedState} />
            <Select ariaLabel="错误状态预览" error onValueChange={() => undefined} options={courseStates} value="" />
            <Select ariaLabel="加载状态预览" loading onValueChange={() => undefined} options={courseStates} value="" />
            <Select ariaLabel="空数据预览" onValueChange={() => undefined} options={[]} value="" />
          </div>
          <div className={styles.choicePreview}>
            <Select ariaLabel="分组选择器预览" onValueChange={setCourseType} options={courseTypes} value={courseType} />
            <Select ariaLabel="多选标签预览" multiple onValueChange={setTeachers} options={courseTeachers} value={teachers} />
            <Select ariaLabel="标签折叠预览" maxTagCount={2} multiple onValueChange={setCollapsedTeachers} options={courseTeachers} value={collapsedTeachers} />
            <Select allowClear ariaLabel="可清空多选框预览" multiple onValueChange={() => undefined} options={courseTeachers} value={teachers} />
          </div>
          <code>src/components/ui/Select</code>
        </article>
        <article className={styles.card}>
          <div><p className={styles.label}>RECOMMENDED</p><h2>Message</h2></div>
          <p className={styles.usage}>按 Figma `5-Message 消息提示` 核对 Primary、Success、Warning、Danger、Info 五种状态，以及 Center off/on 两种对齐；业务默认 3 秒自动消失。</p>
          <div className={styles.messagePreview}>
            {messageTypes.map((type) => <Message duration={0} key={`left-${type}`} message="this is a message." onOpenChange={() => undefined} open type={type} />)}
          </div>
          <div className={styles.messagePreview}>
            {messageTypes.map((type) => <Message align="center" duration={0} key={`center-${type}`} message="this is a message." onOpenChange={() => undefined} open type={type} />)}
          </div>
          <Button onClick={() => setMessageOpen(true)} type="default">显示顶部提示</Button>
          <MessageStack>
            <Message message="优惠券已保存。" onOpenChange={setMessageOpen} open={messageOpen} type="success" />
          </MessageStack>
          <code>src/components/ui/Message</code>
        </article>
        <article className={styles.card}>
          <div><p className={styles.label}>RECOMMENDED</p><h2>Dialog</h2></div>
          <p className={styles.usage}>按 Figma `5-Dialog 对话框` 核对 480 宽、圆角 4、header 74 / body / footer 56，以及遮罩、Escape 关闭与焦点圈定。</p>
          <div className={styles.choicePreview}>
            <Button onClick={() => setDialogOpen(true)} type="default">打开对话框</Button>
            {(['left', 'right', 'top', 'bottom'] as DrawerLayout[]).map((layout) => (
              <Button key={layout} onClick={() => setDrawerLayout(layout)} type="default">{layout} 抽屉</Button>
            ))}
          </div>
          <Dialog
            footer={<><Button onClick={() => setDialogOpen(false)} type="default">取消</Button><Button onClick={() => setDialogOpen(false)}>确定</Button></>}
            onOpenChange={setDialogOpen}
            open={dialogOpen}
            size="sm"
            title="删除优惠券"
          >
            删除后该优惠券将立即失效，已领取的优惠券不受影响。
          </Dialog>
          <Drawer
            footer={<Button onClick={() => setDrawerLayout(null)}>完成</Button>}
            layout={drawerLayout ?? 'right'}
            onOpenChange={(next) => setDrawerLayout(next ? (drawerLayout ?? 'right') : null)}
            open={drawerLayout !== null}
            title="抽屉标题"
          >
            按 Figma Layout 展示 Left / Right 480 宽、Top / Bottom 300 高四种形态。
          </Drawer>
          <code>src/components/ui/Dialog、src/components/ui/Drawer</code>
        </article>
                <article className={styles.card}>
          <div><p className={styles.label}>RECOMMENDED</p><h2>Popover</h2></div>
          <p className={styles.usage}>按 Figma `5-Popover 气泡卡片` 核对 212 宽、内边距 12/16、圆角 4、标题与正文同为 14/22（靠字重 600／400 分主次），以及上下左右共 12 种对位。</p>
          <div className={styles.choicePreview}>
            <Popover content="按创建时间倒序排列，最新的在最前。" title="排序说明"><Button type="default">下左</Button></Popover>
            <Popover align="center" content="居中对齐的气泡卡片。" side="top" title="上中"><Button type="default">上中</Button></Popover>
            <Popover content="右侧展开，用于字段补充说明。" side="right" title="右侧"><Button type="default">右侧</Button></Popover>
            <Popover content="没有标题时仅渲染正文。" side="bottom"><Button type="default">无标题</Button></Popover>
          </div>
          <code>src/components/ui/Popover</code>
        </article>
        <article className={styles.card}>
          <div><p className={styles.label}>RECOMMENDED</p><h2>Tooltip</h2></div>
          <p className={styles.usage}>按 Figma `5-Tooltip 文字提示` 核对 32 高、圆角 4、12/20 字号与 Light／Dark 两种效果及箭头对位。</p>
          <div className={styles.choicePreview}>
            <Tooltip content="默认深色提示"><Button type="default">深色提示</Button></Tooltip>
            <Tooltip content="浅色提示效果" effect="light"><Button type="default">浅色提示</Button></Tooltip>
            <Tooltip align="start" content="起点对齐" side="bottom"><Button type="default">下方向</Button></Tooltip>
            <Tooltip align="center" content="右侧提示" side="right"><Button type="default">右方向</Button></Tooltip>
          </div>
          <code>src/components/ui/Tooltip</code>
        </article>
        <article className={styles.card}>
          <div><p className={styles.label}>RECOMMENDED</p><h2>Popconfirm</h2></div>
          <p className={styles.usage}>按 Figma `5-Popconfirm 气泡确认框` 核对 211 宽、内边距 16、间距 16、圆角 4、文案 20 高与操作行 24 高（small 按钮）。</p>
          <div className={styles.choicePreview}>
            <Popconfirm onConfirm={() => undefined} title="确定删除该优惠券？"><Button type="danger">删除</Button></Popconfirm>
            <Popconfirm description="停用后已领取的券不受影响。" onConfirm={() => undefined} title="确定停用该优惠券？"><Button type="default">停用</Button></Popconfirm>
            <Popconfirm align="center" onConfirm={() => undefined} side="top" title="确定提交审核？"><Button type="default">上方确认</Button></Popconfirm>
          </div>
          <code>src/components/ui/Popconfirm</code>
        </article>
        <article className={styles.card}>
          <div><p className={styles.label}>RECOMMENDED</p><h2>Tour 新手引导</h2></div>
          <p className={styles.usage}>高亮挖空遮罩 + 跟随目标的气泡。遮罩与气泡视觉全走本地组件与 token，只借 Base UI 的定位能力（翻转、避让、跟随滚动）。点上方的「开始引导」体验三步流程：上一步／下一步／跳过、步骤计数、Esc 或点遮罩退出、目标不在视野时自动滚动定位。</p>
          <div className={styles.choicePreview}><Button onClick={() => setTourActive(true)} size="small">开始引导</Button></div>
          <div className={styles.choicePreview}>
            <span data-tour="tour-demo-a"><Button size="small" type="default">目标 A</Button></span>
            <span data-tour="tour-demo-b"><Button size="small" type="default">目标 B</Button></span>
            <span data-tour="tour-demo-c"><Button size="small" type="default">目标 C</Button></span>
          </div>
          {tourActive ? <Tour active finishText="知道了" onFinish={() => setTourActive(false)} steps={tourSteps} /> : null}
          <code>src/components/ui/Tour</code>
        </article>
        <article className={styles.card}>
          <div><p className={styles.label}>RECOMMENDED</p><h2>Link</h2></div>
          <p className={styles.usage}>按 Figma `1-Link 链接` 核对六种语义、常态／悬停（-5 阶变亮）／悬停下划线／禁用四种状态，以及左右图标槽位。</p>
          <div className={styles.choicePreview}>
            {linkTypes.map((type) => <DesignLink key={type} type={type}>{type}</DesignLink>)}
          </div>
          <div className={styles.choicePreview}>
            <DesignLink>悬停变色，无下划线</DesignLink>
            <DesignLink disabled>不可用</DesignLink>
            <DesignLink leadingIcon="a-arrow-left">返回列表</DesignLink>
            <DesignLink href="#/components">跳转链接</DesignLink>
          </div>
          <code>src/components/ui/Link</code>
        </article>
        <article className={styles.card}>
          <div><p className={styles.label}>RECOMMENDED</p><h2>Table</h2></div>
          <p className={styles.usage}>按 Figma `3-Table 表格` 核对列宽、排序、筛选、多选、当前行、加载、汇总、横纵滚动与固定列反馈。</p>
          <div className={styles.choicePreview}><Button onClick={() => setTableLoading((value) => !value)} size="small">切换加载态</Button></div>
          <Table ariaLabel="优惠券表格预览" columns={tablePreviewColumns} currentKey={tableCurrent} loading={tableLoading} onCurrentChange={setTableCurrent} onSortChange={(key, order) => setTableSort({ key, order })} pagination={{ ariaLabel: '表格分页预览', onPageChange: setTablePage, page: tablePage, pageSize: 1 }} rowKey={(row) => row.id} filterValues={tableFilter} onFilterChange={(key, values) => { setTableFilter((current) => ({ ...current, [key]: [...values] })); setTablePage(1); }} onSelectionChange={(keys) => setTableSelection([...keys])} selectable selectedKeys={tableSelection} rows={tableRows} sortKey={tableSort.key} sortOrder={tableSort.order} summary={() => ['合计', '', '', '当前页 1 张券']} />
          <Table ariaLabel="斑马纹预览" columns={tablePreviewColumns} rowKey={(row) => row.id} rows={tablePreviewRows} stripe />
          <Table ariaLabel="空状态预览" columns={tablePreviewColumns} emptyText="暂无优惠券" rowKey={(row) => row.id} rows={[]} />
          <code>src/components/ui/Table</code>
        </article>
        <article className={styles.card}>
          <div><p className={styles.label}>RECOMMENDED</p><h2>Tag</h2></div>
          <p className={styles.usage}>按 Figma `3-Tag 标签` 核对 Large 28 / Default 22 / Small 20 三种尺寸、五种功能类型与 Plain／Light／Dark 三种主题；业务分类使用独立的低饱和蓝／紫／青配色。</p>
          <div className={styles.choicePreview}>
            {tagTypes.map((type) => <Tag key={type} type={type}>标签</Tag>)}
          </div>
          <div className={styles.choicePreview}>
            {tagTypes.map((type) => <Tag key={`light-${type}`} topic="light" type={type}>标签</Tag>)}
            {tagTypes.map((type) => <Tag key={`plain-${type}`} topic="plain" type={type}>标签</Tag>)}
            {tagTypes.map((type) => <Tag key={`dark-${type}`} topic="dark" type={type}>标签</Tag>)}
          </div>
          <div className={styles.choicePreview}>
            {tagCategories.map((category) => <Tag category={category} key={category}>分类标签</Tag>)}
          </div>
          <div className={styles.choicePreview}>
            <Tag size="large">上海</Tag>
            <Tag>上海</Tag>
            <Tag size="small">上海</Tag>
            <Tag closable onClose={() => undefined}>可移除</Tag>
          </div>
          <code>src/components/ui/Tag</code>
        </article>
        <article className={styles.card}>
          <div><p className={styles.label}>RECOMMENDED</p><h2>Pagination</h2></div>
          <p className={styles.usage}>完整实例包含总数范围、页码、省略、每页条数与快速跳页；状态与数据由外部受控，视觉按 Figma 的 Default 32 / Small 24、Plain / Background 组合校对。</p>
          <div className={styles.choicePreview}>
            <Pagination align="end" onPageChange={(page) => setPaginationPreview((current) => ({ ...current, page }))} onPageSizeChange={(page, pageSize) => setPaginationPreview({ page, pageSize })} page={paginationPreview.page} pageSize={paginationPreview.pageSize} showQuickJumper={{ goButton: '确定' }} showSizeChanger showTotal total={85} />
          </div>
          <div className={styles.choicePreview}>
            <Pagination onPageChange={() => undefined} page={10} size="small" total={200} variant="background" />
            <Pagination onPageChange={() => undefined} page={3} simple total={85} />
            <Pagination onPageChange={() => undefined} page={10} showLessItems total={200} />
            <Pagination disabled onPageChange={() => undefined} page={3} total={85} />
            <Pagination hideOnSinglePage onPageChange={() => undefined} page={1} total={8} />
          </div>
          <code>src/components/ui/Pagination</code>
        </article>
        <article className={styles.card}>
          <div><p className={styles.label}>RECOMMENDED</p><h2>ColorPicker</h2></div>
          <p className={styles.usage}>实时预览、关闭或确定后提交；支持 HEX、方向键选色与浏览器吸管。</p>
          <div className={styles.choicePreview}><ColorPicker ariaLabel="颜色选择预览" onValueChange={setPickerColor} value={pickerColor} /><code>{pickerColor}</code><ColorPicker ariaLabel="禁用颜色选择预览" disabled onValueChange={() => undefined} value="#CDDEFA" /></div>
          <code>src/components/ui/ColorPicker</code>
        </article>
        <article className={styles.card}>
          <div><p className={styles.label}>CONTROLLED</p><h2>Icon</h2></div>
          <div className={styles.icons}>{iconNames.map((name) => <div key={name}><Icon label={name} name={name} size="lg" /><code title={name}>{name}</code></div>)}</div>
          <code>src/components/ui/Icon</code>
        </article>
      </div>
    </section>
  </main>;
}
